import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Fingerprint } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Tooltip } from '../components/Tooltip';
import { NotificationPermissionBanner } from '../components/NotificationPermissionBanner';

function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkBiometricAvailability();
    // Load saved credentials if they exist
    const savedCredentials = localStorage.getItem('ajel_credentials');
    if (savedCredentials) {
      const { phone: savedPhone, password: savedPassword } = JSON.parse(savedCredentials);
      setPhone(savedPhone);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const checkBiometricAvailability = async () => {
    try {
      // Check if the device supports biometric authentication
      if ('credentials' in navigator && window.PublicKeyCredential) {
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        setBiometricAvailable(available);
      } else {
        console.log('WebAuthn not supported on this device');
        setBiometricAvailable(false);
      }
    } catch (err) {
      console.error('Error checking biometric availability:', err);
      setBiometricAvailable(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      console.log('=== LOGIN ATTEMPT START ===');
      console.log('Phone:', phone);
      console.log('Email format:', `${phone}@ajel.life`);

      const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
        email: `${phone}@ajel.life`,
        password,
      });

      console.log('Auth result:', authData);
      console.log('Auth error:', signInError);

      if (signInError) {
        console.error('Sign in error:', signInError);
        throw signInError;
      }

      if (!authData.user) {
        console.error('No user in auth data');
        throw new Error('لم يتم العثور على المستخدم');
      }

      console.log('User authenticated successfully');
      console.log('User ID:', authData.user.id);
      console.log('User email:', authData.user.email);

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select()
        .eq('id', authData.user.id)
        .maybeSingle();

      console.log('Profile:', profile);
      console.log('Profile error:', profileError);

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('Profile fetch error:', profileError);
        throw profileError;
      }

      if (!profile) {
        console.error('No profile found for user');
        await supabase.auth.signOut();
        setError('الحساب غير موجود. الرجاء التسجيل أولاً');
        return;
      }

      console.log('Login successful!');

      // Save credentials if remember me is checked
      if (rememberMe) {
        localStorage.setItem('ajel_credentials', JSON.stringify({ phone, password }));
      } else {
        localStorage.removeItem('ajel_credentials');
      }

      navigate('/home', { replace: true });
    } catch (err) {
      console.error('=== LOGIN ERROR ===');
      console.error('Error:', err);
      setError(err instanceof Error ? err.message : 'رقم الهاتف أو كلمة المرور غير صحيحة');
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricAuth = async () => {
    try {
      if (!biometricAvailable) {
        alert('جهازك لا يدعم المصادقة البيومترية');
        return;
      }

      // Create challenge
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
        challenge,
        rpId: window.location.hostname,
        allowCredentials: [],
        userVerification: 'required',
        timeout: 60000
      };

      try {
        const assertion = await navigator.credentials.get({
          publicKey: publicKeyCredentialRequestOptions
        });

        if (assertion) {
          // Here you would normally validate the assertion with your server
          // For demo purposes, we'll just redirect to home
          navigate('/home', { replace: true });
        }
      } catch (err) {
        console.error('Biometric authentication failed:', err);
        if (err instanceof Error) {
          if (err.name === 'NotAllowedError') {
            alert('تم إلغاء المصادقة البيومترية من قبل المستخدم');
          } else if (err.name === 'SecurityError') {
            alert('فشلت المصادقة البيومترية لأسباب أمنية');
          } else {
            alert('فشلت المصادقة البيومترية. الرجاء المحاولة مرة أخرى أو استخدام كلمة المرور');
          }
        } else {
          alert('فشلت المصادقة البيومترية. الرجاء المحاولة مرة أخرى أو استخدام كلمة المرور');
        }
      }
    } catch (error) {
      console.error('Biometric setup error:', error);
      alert('حدث خطأ في إعداد المصادقة البيومترية. الرجاء المحاولة مرة أخرى');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col">
      {/* Notification Permission Banner */}
      <NotificationPermissionBanner />
      
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm bg-white rounded-3xl shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-gray-800 mb-2">مرحباً بعودتك!</h1>
            <div className="flex items-center justify-center space-x-2">
              <p className="text-gray-600">دفتر حسابات اجل</p>
              <Tooltip text="تطبيق لإدارة الحسابات والديون بطريقة سهلة وآمنة" />
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="relative flex items-center">
              <input
                type="tel"
                placeholder="أدخل رقم الهاتف"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 text-left pl-12"
                required
              />
              <div className="absolute left-4">
                <Tooltip text="أدخل رقم الهاتف المسجل في حسابك" position="right" />
              </div>
            </div>

            <div className="relative flex items-center">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="كلمة المرور"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 text-left pl-12 pr-12"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4"
              >
                {showPassword ? <EyeOff size={20} className="text-gray-400" /> : <Eye size={20} className="text-gray-400" />}
              </button>
              <div className="absolute left-4">
                <Tooltip text="أدخل كلمة المرور الخاصة بحسابك" position="right" />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 text-blue-500 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">تذكرني</span>
                </label>
                <Tooltip text="حفظ بيانات الدخول لتسجيل الدخول تلقائياً في المرة القادمة" />
              </div>
              <button 
                type="button"
                onClick={() => navigate('/reset-password')}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                استعادة كلمة المرور
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#F87171] text-white rounded-xl hover:bg-[#EF4444] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
            </button>
          </form>

          <div className="mt-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">أو المتابعة باستخدام</span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-4">
              <button className="flex items-center justify-center p-3 border border-gray-200 rounded-xl hover:bg-gray-50">
                <img src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_272x92dp.png" alt="Google" className="h-5 object-contain" />
              </button>
              <button 
                onClick={handleBiometricAuth}
                disabled={!biometricAvailable}
                className={`flex items-center justify-center p-3 border border-gray-200 rounded-xl ${
                  biometricAvailable ? 'hover:bg-gray-50' : 'opacity-50 cursor-not-allowed'
                }`}
                title={biometricAvailable ? 'استخدم المصادقة البيومترية' : 'المصادقة البيومترية غير متوفرة على هذا الجهاز'}
              >
                <Fingerprint className="h-5 w-5 text-gray-600" />
              </button>
              <button className="flex items-center justify-center p-3 border border-gray-200 rounded-xl hover:bg-gray-50">
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/0/05/Facebook_Logo_%282019%29.png/1024px-Facebook_Logo_%282019%29.png" alt="Facebook" className="h-5 object-contain" />
              </button>
            </div>
          </div>

          <p className="mt-8 text-center text-sm text-gray-500">
            ليس لديك حساب؟{' '}
            <button 
              onClick={() => navigate('/register')} 
              className="text-blue-500 hover:text-blue-600"
            >
              سجل الآن
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;